<div class="competitor-finder-container">
  <div class="header-section">
    <h1>Competitor Finder</h1>
    <p class="subtitle">Enter one domain and get ranked competitors with quick crawl actions.</p>
  </div>

  <div class="controls-row">
    <div class="field">
      <label class="field__label" for="domain">Domain</label>
      <input class="input"
        id="domain"
        type="text"
        [(ngModel)]="domain"
        (blur)="previewDiscovery()"
        placeholder="example.com"
        [disabled]="loading"
      />
    </div>

    <div class="field narrow">
      <label class="field__label" for="limit">Max competitors</label>
      <input class="input"
        id="limit"
        type="number"
        min="1"
        max="50"
        [(ngModel)]="limit"
        [disabled]="loading"
      />
    </div>

    <div class="field narrow">
      <label class="field__label" for="maxDomainSitemapPages">Max sitemap pages (true count)</label>
      <input class="input"
        id="maxDomainSitemapPages"
        type="number"
        min="1"
        step="1"
        [(ngModel)]="maxDomainSitemapPages"
        [disabled]="loading"
      />
    </div>

    <div class="button-group">
      <button class="btn btn--accent" (click)="run(false)" [disabled]="loading || !domain">
        {{ loading ? 'Running...' : 'Run Competitor Finder' }}
      </button>
      <button class="btn" (click)="run(true)" [disabled]="loading || !domain">
        Force Refresh
      </button>
    </div>
  </div>

  <div class="saved-runs-section">
    <div class="saved-runs-header">
      <h3>Saved Runs</h3>
      <button class="btn" (click)="loadSavedRuns()" [disabled]="loading || savedRunsLoading">
        {{ savedRunsLoading ? 'Refreshing...' : 'Refresh List' }}
      </button>
    </div>
    <div class="saved-runs-controls">
      <select class="input" [(ngModel)]="selectedSavedRunId" [disabled]="loading || savedRunsLoading || savedRuns.length === 0">
        <option value="" disabled>Select a completed run</option>
        <option *ngFor="let run of savedRuns" [value]="run.id">
          {{ getSavedRunLabel(run) }}
        </option>
      </select>
      <button class="btn btn--accent" (click)="loadSelectedRun()" [disabled]="loading || loadingSavedRun || !selectedSavedRunId">
        {{ loadingSavedRun ? 'Loading...' : 'Open Run' }}
      </button>
    </div>
  </div>

  <div class="assisted-section">
    <h3>PageAnalysis-Assisted Discovery</h3>
    <div class="controls-row">
      <div class="button-group">
        <button class="btn" (click)="previewDiscovery()" [disabled]="loading || !domain || previewLoading">
          {{ previewLoading ? 'Previewing...' : 'Preview Search + Cost' }}
        </button>
      </div>
    </div>

    <div class="assisted-source-help">
      <small>
        <strong>Mode:</strong> SERP competitors + ranked keywords (always on). This broadens discovery by expanding selected topics with ranked keywords first, then finding SERP competitors from that set.
      </small>
    </div>

    <div class="topic-workspace">
      <div
        class="topic-box selected"
        (drop)="onTopicDropToSelected($event)"
        (dragover)="allowTopicDrop($event)"
      >
        <div class="topic-box-title">Selected topics (used in assisted search)</div>
        <div class="topic-list">
          <span
            class="badge badge--pill badge--solid badge--accent topic-chip"
            [class.pending-swap]="isPendingSwap(topic, 'selected')"
            *ngFor="let topic of selectedAssistedTopics"
            draggable="true"
            (dragstart)="onTopicDragStart(topic)"
            (click)="onTopicChipClick(topic, 'selected')"
            (dblclick)="removeTopicFromSelection(topic)"
          >
            {{ topic }} <button type="button" class="btn btn--bare btn--sm" (click)="removeTopicFromSelection(topic); $event.stopPropagation()" [disabled]="loading">x</button>
          </span>
          <small *ngIf="selectedAssistedTopics.length === 0">Drag topics here, click + from pool, or click one topic in each box to swap.</small>
        </div>
      </div>

      <div
        class="topic-box available"
        (drop)="onTopicDropToAvailable($event)"
        (dragover)="allowTopicDrop($event)"
      >
        <div class="topic-box-title">Available topics (next 20)</div>
        <div class="topic-list">
          <span
            class="badge badge--pill badge--solid topic-chip"
            [class.pending-swap]="isPendingSwap(topic, 'available')"
            *ngFor="let topic of availableAssistedTopics"
            draggable="true"
            (dragstart)="onTopicDragStart(topic)"
            (click)="onTopicChipClick(topic, 'available')"
            (dblclick)="addTopicToSelection(topic)"
          >
            {{ topic }} <button type="button" class="btn btn--bare btn--sm" (click)="addTopicToSelection(topic); $event.stopPropagation()" [disabled]="loading">+</button>
          </span>
          <small *ngIf="availableAssistedTopics.length === 0">No extra topics available yet. Run preview first.</small>
        </div>
      </div>
    </div>
  </div>

  <div class="error-message" *ngIf="error">{{ error }}</div>

  <div class="run-progress" *ngIf="loading || runProgress > 0">
    <div class="run-progress-header">
      <small>{{ runProgressLabel || 'Running...' }}</small>
      <small>{{ runProgress | number:'1.0-0' }}%</small>
    </div>
    <div class="run-progress-track">
      <div class="run-progress-fill" [style.width.%]="runProgress"></div>
    </div>
    <small class="run-progress-detail" *ngIf="runProgressDetail">{{ runProgressDetail }}</small>
  </div>

  <div class="run-meta" *ngIf="domain">
    <span>
      PageAnalysis-assisted:
      <strong *ngIf="assistedAvailability === true">available</strong>
      <strong *ngIf="assistedAvailability === false">not available</strong>
      <strong *ngIf="assistedAvailability == null">unknown</strong>
    </span>
    <span *ngIf="estimatedCosts">Estimated cost: <strong>{{ formatCurrency(estimatedCosts.total) }}</strong></span>
  </div>

  <div class="run-meta" *ngIf="searchPlanKeywords.length > 0">
    <span>Will search:</span>
    <span><strong>{{ searchPlanKeywords.join(', ') }}</strong></span>
  </div>

  <div class="run-meta" *ngIf="inputKeywordPerformance as perf">
    <span>Input keyword est. traffic: <strong>{{ perf.totalEstimatedTraffic | number:'1.0-2' }}</strong></span>
    <span>Matched ranked keywords: <strong>{{ perf.matchedRankedKeywords }}</strong></span>
    <span>Total search volume: <strong>{{ perf.totalSearchVolume | number:'1.0-0' }}</strong></span>
    <span>Avg position: <strong>{{ perf.avgPosition != null ? (perf.avgPosition | number:'1.0-2') : 'N/A' }}</strong></span>
  </div>
  <details class="run-input-performance" *ngIf="inputKeywordPerformance?.byInputKeyword?.length">
    <summary>Input keyword breakdown</summary>
    <div class="why-keywords">
      <small *ngFor="let item of inputKeywordPerformance?.byInputKeyword">
        {{ item.keyword }}: traffic {{ item.totalEstimatedTraffic | number:'1.0-2' }}, volume {{ item.totalSearchVolume | number:'1.0-0' }}, avg pos {{ item.avgPosition != null ? (item.avgPosition | number:'1.0-2') : 'N/A' }}
      </small>
    </div>
  </details>

  <div class="run-meta" *ngIf="rows.length > 0">
    <span>Source: <strong>{{ metadata.dataSource || 'N/A' }}</strong></span>
    <span>Updated: <strong>{{ formatDate(metadata.lastUpdated) }}</strong></span>
    <span>Max sitemap pages: <strong>{{ metadata.maxDomainSitemapPages || 'none' }}</strong></span>
    <span>Scoring: <strong>full weighted</strong></span>
    <span>Broad platforms: <strong>excluded</strong></span>
    <span>Assisted source: <strong>SERP + ranked keywords</strong></span>
    <span *ngIf="stats">Candidates: <strong>{{ stats.totalCandidates }}</strong></span>
    <span *ngIf="stats && (stats.excludedByDenylist || 0) > 0">Excluded: <strong>{{ stats.excludedByDenylist }}</strong></span>
    <span *ngIf="stats && (stats.excludedBySitemapSize || 0) > 0">Excluded (sitemap): <strong>{{ stats.excludedBySitemapSize }}</strong></span>
    <span *ngIf="stats && (stats.sitemapCheckTimeouts || 0) > 0">Sitemap timeouts: <strong>{{ stats.sitemapCheckTimeouts }}</strong></span>
    <span *ngIf="costBreakdown">Cost: <strong>{{ formatCurrency(costBreakdown.total) }}</strong></span>
  </div>

  <div class="results-section" *ngIf="assistedRows.length > 0">
    <h3>PageAnalysis-Assisted Competitors</h3>
    <div class="run-meta" *ngIf="assistedStats">
      <span>Assisted candidates: <strong>{{ assistedStats.totalCandidates }}</strong></span>
      <span>Assisted rows: <strong>{{ assistedStats.totalRows }}</strong></span>
    </div>
    <table class="table results-table assisted">
      <colgroup>
        <col class="col-domain">
        <col class="col-score">
        <col class="col-traffic">
        <col class="col-avg">
        <col class="col-sitemap">
        <col class="col-crawl">
      </colgroup>
      <thead>
        <tr>
          <th>Domain</th>
          <th>Assisted Score</th>
          <th>Organic Traffic</th>
          <th>Avg Position</th>
          <th>Sitemap Pages</th>
          <th>Crawl</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of assistedRows">
          <td class="domain-cell">
            <div class="domain-cell-content">
              <a
                class="domain-link"
                [href]="getDomainHomepageUrl(row.domain)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ row.domain }}
              </a>
              <button
                type="button"
                class="btn btn--bare btn--sm remove-row-btn"
                title="Hide from this run"
                *ngIf="canRemoveRow(row.domain)"
                (click)="removeRow(row.domain, 'assistedRows')"
                [disabled]="isRemovingRow(row.domain, 'assistedRows')"
              >
                {{ isRemovingRow(row.domain, 'assistedRows') ? '...' : 'x' }}
              </button>
            </div>
          </td>
          <td>
            <div>{{ row.score | number:'1.0-2' }}</div>
            <small>{{ row.intersections }} topic keyword matches</small>
            <details class="why-match" *ngIf="(row.matchDetails?.length || row.matchKeywords?.length || 0) > 0">
              <summary>Why match</summary>
              <div class="why-keywords">
                <small *ngFor="let detail of (row.matchDetails || [])">
                  {{ detail.keyword }} (position {{ detail.position != null ? detail.position : 'N/A' }})
                </small>
                <ng-container *ngIf="(!row.matchDetails || row.matchDetails.length === 0)">
                  <small *ngFor="let keyword of row.matchKeywords">
                    {{ keyword }} (position N/A)
                  </small>
                </ng-container>
              </div>
            </details>
          </td>
          <td>
            <div>{{ row.organicTraffic != null ? (row.organicTraffic | number:'1.0-0') : 'N/A' }}</div>
          </td>
          <td>{{ row.avgPosition != null ? (row.avgPosition | number:'1.0-1') : 'N/A' }}</td>
          <td>{{ row.sitemapPages != null ? row.sitemapPages : 'N/A' }}</td>
          <td class="crawl-cell">
            <div class="crawl-actions">
              <button class="btn btn--sm" (click)="estimateCrawl(row.domain)" [disabled]="crawlEstimateLoading.get(row.domain)">
                {{ crawlEstimateLoading.get(row.domain) ? 'Estimating...' : 'Estimate' }}
              </button>
              <button class="btn btn--sm" (click)="startCrawl(row.domain)" [disabled]="crawlStarting.get(row.domain)">
                {{ crawlStarting.get(row.domain) ? 'Starting...' : 'Crawl' }}
              </button>
            </div>
            <div class="crawl-meta" *ngIf="crawlEstimates.get(row.domain) as estimate">
              <small *ngIf="estimate.aliasApplied && estimate.canonicalDomain">
                Canonical domain: {{ estimate.canonicalDomain }} (from {{ row.domain }})
              </small>
              <small>Sitemap: {{ estimate.totalPagesInSitemap }} pages</small>
              <small>Est. cost: ${{ estimate.estimatedTotalCost | number:'1.2-2' }}</small>
            </div>
            <div class="crawl-meta" *ngIf="getCrawlStatus(row.domain) as status">
              <small *ngIf="status.aliasApplied && status.canonicalDomain">
                Canonical domain: {{ status.canonicalDomain }} (from {{ row.domain }})
              </small>
              <small>Status: {{ getCrawlStatusLabel(row.domain) }} ({{ status.progress }}%)</small>
              <small *ngIf="status.crawlCost != null && status.crawlCost > 0">Crawl cost: {{ formatCurrency(status.crawlCost) }}</small>
            </div>
            <div class="crawl-error" *ngIf="crawlEstimateError.get(row.domain)">{{ crawlEstimateError.get(row.domain) }}</div>
            <div class="crawl-error" *ngIf="crawlStatusError.get(row.domain)">{{ crawlStatusError.get(row.domain) }}</div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="results-section" *ngIf="rows.length > 0">
    <h3>Intersection-Based Competitors</h3>
    <table class="table results-table main">
      <colgroup>
        <col class="col-domain">
        <col class="col-score">
        <col class="col-traffic">
        <col class="col-coverage">
        <col class="col-authority">
        <col class="col-urls">
        <col class="col-freshness">
        <col class="col-source">
        <col class="col-crawl">
      </colgroup>
      <thead>
        <tr>
          <th>Domain</th>
          <th>Rank Score</th>
          <th>Organic Traffic</th>
          <th>Coverage</th>
          <th>Authority / Tier</th>
          <th>Top URLs</th>
          <th>Freshness</th>
          <th>Source</th>
          <th>Crawl</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of rows">
          <td class="domain-cell">
            <div class="domain-cell-content">
              <a
                class="domain-link"
                [href]="getDomainHomepageUrl(row.domain)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ row.domain }}
              </a>
              <button
                type="button"
                class="btn btn--bare btn--sm remove-row-btn"
                title="Hide from this run"
                *ngIf="canRemoveRow(row.domain)"
                (click)="removeRow(row.domain, 'rows')"
                [disabled]="isRemovingRow(row.domain, 'rows')"
              >
                {{ isRemovingRow(row.domain, 'rows') ? '...' : 'x' }}
              </button>
            </div>
          </td>
          <td>
            <div>{{ row.score | number:'1.0-2' }}</div>
            <small>{{ row.intersections }} intersections</small>
            <small *ngIf="(row.discoverySources?.length || 0) > 0">Source: {{ row.discoverySources?.join(', ') }}</small>
            <small *ngIf="row.weightedIntersections != null">Weighted: {{ row.weightedIntersections | number:'1.0-2' }}</small>
            <details class="why-match" *ngIf="hasIntersectionDetails(row)">
              <summary>Why match</summary>
              <small *ngIf="row.intersectionBandCounts as bands">
                Bands A/B/C/D: {{ bands.bandA }} / {{ bands.bandB }} / {{ bands.bandC }} / {{ bands.bandD }}
              </small>
              <div class="why-keywords" *ngIf="(row.intersectionSamples?.length || 0) > 0">
                <small *ngFor="let sample of row.intersectionSamples">
                  {{ sample.keyword }} (us #{{ sample.firstRank || '—' }}, them #{{ sample.secondRank || '—' }}, {{ sample.band }}<span *ngIf="sample.searchVolume != null">, vol {{ sample.searchVolume }}</span>)
                </small>
              </div>
            </details>
          </td>
          <td>
            <div>{{ row.organicTraffic != null ? (row.organicTraffic | number:'1.0-0') : 'N/A' }}</div>
          </td>
          <td>
            <ng-container *ngIf="row.coveragePercentage != null; else noCoverage">
              <div>{{ row.coveragePercentage | number:'1.0-1' }}%</div>
              <small>{{ row.entitiesCovered || 0 }} / {{ row.totalEntities || 0 }}</small>
            </ng-container>
            <ng-template #noCoverage>
              <span>N/A</span>
            </ng-template>
          </td>
          <td>
            <ng-container *ngIf="row.authorityScore != null; else noAuthority">
              <div>{{ row.authorityScore | number:'1.0-1' }}</div>
              <small>{{ row.topicTier || 'unclassified' }}</small>
            </ng-container>
            <ng-template #noAuthority>
              <span>N/A</span>
            </ng-template>
          </td>
          <td>
            <div class="urls-list" *ngIf="row.topUrls.length > 0; else noUrls">
              <a *ngFor="let topUrl of row.topUrls; let i = index"
                 [href]="topUrl.url"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="url-item">
                {{ i + 1 }}. {{ topUrl.title || topUrl.url }}
              </a>
            </div>
            <ng-template #noUrls>
              <span>N/A</span>
            </ng-template>
          </td>
          <td>{{ formatDate(row.lastUpdated) }}</td>
          <td>
            <span class="badge" [class.badge--accent]="row.dataSource === 'api'" [class.badge--success]="row.dataSource === 'database'">
              {{ row.dataSource }}
            </span>
          </td>
          <td class="crawl-cell">
            <div class="crawl-actions">
              <button class="btn btn--sm" (click)="estimateCrawl(row.domain)" [disabled]="crawlEstimateLoading.get(row.domain)">
                {{ crawlEstimateLoading.get(row.domain) ? 'Estimating...' : 'Estimate' }}
              </button>
              <button class="btn btn--sm" (click)="startCrawl(row.domain)" [disabled]="crawlStarting.get(row.domain)">
                {{ crawlStarting.get(row.domain) ? 'Starting...' : 'Crawl' }}
              </button>
            </div>
            <div class="crawl-meta" *ngIf="crawlEstimates.get(row.domain) as estimate">
              <small *ngIf="estimate.aliasApplied && estimate.canonicalDomain">
                Canonical domain: {{ estimate.canonicalDomain }} (from {{ row.domain }})
              </small>
              <small>Sitemap: {{ estimate.totalPagesInSitemap }} pages</small>
              <small>Est. cost: ${{ estimate.estimatedTotalCost | number:'1.2-2' }}</small>
            </div>
            <div class="crawl-meta" *ngIf="getCrawlStatus(row.domain) as status">
              <small *ngIf="status.aliasApplied && status.canonicalDomain">
                Canonical domain: {{ status.canonicalDomain }} (from {{ row.domain }})
              </small>
              <small>Status: {{ getCrawlStatusLabel(row.domain) }} ({{ status.progress }}%)</small>
              <small *ngIf="status.crawlCost != null && status.crawlCost > 0">Crawl cost: {{ formatCurrency(status.crawlCost) }}</small>
            </div>
            <div class="crawl-error" *ngIf="crawlEstimateError.get(row.domain)">{{ crawlEstimateError.get(row.domain) }}</div>
            <div class="crawl-error" *ngIf="crawlStatusError.get(row.domain)">{{ crawlStatusError.get(row.domain) }}</div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
